import { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "components/Sidenav";
import themeRTL from "assets/theme/theme-rtl";
import themeDarkRTL from "assets/theme-dark/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import routes from "routes";
import { useMaterialUIController, setMiniSidenav } from "helpers/context";
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import SignIn from "./routes/authentication/sign-in";
import { validateToken } from "./helpers/functions/functions";
import AppConfig from "./helpers/constants/AppConfig";
import RouteUrls from "./helpers/urls/RouteUrls";
import ForgotPassword from "./routes/authentication/forgot-password";
import ChangePassword from "./routes/authentication/change-password";
import DashboardNavbar from "./components/Navbars/DashboardNavbar";
import MDBox from "./components/MDs/MDBox";
import Dashboard from "./routes/dashboard";
import EmptyLayout from "./components/LayoutContainers/EmptyLayout";
import PagePermBits from "./helpers/constants/PagePermBits";

function App({ user_permission, tokenTimer }) {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  const userType = JSON.parse(localStorage.getItem("user"))?.type;

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };
  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // scroll to top of the page
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // returns all the routes that user has the permission to access to
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.permission & user_permission) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      } else if (route.key === "dashboard") {
        if (!(userType == 2 && (user_permission & PagePermBits.reports))) {
          return <Route exact path={"/"} element={<EmptyLayout />} key={route.key} />;
        }
      }

      return null;
    });

  // checks if token is still valid, if not redirects to sign-in page
  const isTokenValidated = validateToken(tokenTimer);

  return isTokenValidated ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />

        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName={AppConfig.app_name}
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />

            <MDBox
              sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
                p: 3,
                pb: 0,
                position: "relative",
                [breakpoints.up("xl")]: {
                  marginLeft: miniSidenav ? pxToRem(120) : pxToRem(274),
                  transition: transitions.create(["margin-left", "margin-right"], {
                    easing: transitions.easing.easeInOut,
                    duration: transitions.duration.standard,
                  }),
                },
              })}
            >
              <DashboardNavbar />
            </MDBox>
          </>
        )}

        <Routes>
          {getRoutes(routes)}
          <Route exact path={RouteUrls.auth.login} element={<SignIn />} key="sign-in" />
          <Route path="*" element={<Navigate to={RouteUrls.dashboard} />} />
        </Routes>

      </ThemeProvider>
    </CacheProvider>
  ) : (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />

        <Routes>
          <Route exact path={RouteUrls.auth.login} element={<SignIn />} key="sign-in" />
          <Route exact path={RouteUrls.auth.forgot_password} element={<ForgotPassword />} key="forgot-password" />
          <Route exact path={RouteUrls.auth.change_password} element={<ChangePassword />} key="change-password" />
          <Route path="*" element={<Navigate to="/sign-in" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  )
}

const mapStateToProps = state => ({
  user_permission: state.user_permission,
  tokenTimer: state.tokenTimer
});

export default connect(mapStateToProps)(App);